import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { metricsApi, type DailyAgg, type MetricsHourly } from "@/api/metrics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { CostCurveWidget } from "@/components/CostCurveWidget";
import { calendarRangeFor, fmt, hourFromTs, hourLabelShifted, num, shiftPeriod, todayIso, type Period } from "@/lib/format";
import { cn } from "@/lib/utils";

type ViewMode = "pair" | "plan" | "fact" | "delta";
type RangeMode = "day" | Period;

const hourLabel = (ts: string): string => hourLabelShifted(hourFromTs(ts));

/** Зсунути ISO-дату на N днів (повертає YYYY-MM-DD). */
function shiftDate(iso: string, deltaDays: number): string {
  const d = new Date(iso);
  d.setDate(d.getDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}

export function HomePage1() {
  const { t } = useTranslation();
  const [date, setDate] = useState(todayIso());
  const [range, setRange] = useState<RangeMode>("day");
  const [view, setView] = useState<ViewMode>("pair");

  // hourly — лише в режимі "day"; daily — для решти.
  const hourly = useQuery({
    queryKey: ["metrics-hourly", date],
    queryFn: () => metricsApi.hourly(date),
    enabled: range === "day",
  });
  const dailyRange = useMemo(
    () => (range === "day" ? null : calendarRangeFor(date, range)),
    [range, date],
  );
  const daily = useQuery({
    queryKey: ["metrics-daily-h1", dailyRange?.from, dailyRange?.to],
    queryFn: () => metricsApi.daily(dailyRange!.from, dailyRange!.to),
    enabled: dailyRange !== null,
  });

  const isLoading = range === "day" ? hourly.isLoading : daily.isLoading;
  const hourlyRows = hourly.data ?? [];
  const dailyRows = daily.data ?? [];
  const hasData = range === "day" ? hourlyRows.length > 0 : dailyRows.length > 0;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t("home1.title")}</CardTitle>
          <CardDescription>{t("home1.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          {/* День: input + стрілки prev/next */}
          <div className="space-y-1">
            <Label>{t("plan.date")}</Label>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDate((d) => range === "day" ? shiftDate(d, -1) : shiftPeriod(d, range, -1))}
                aria-label={t("home1.prev_day")}
                title={t("home1.prev_day")}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-[160px]"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDate((d) => range === "day" ? shiftDate(d, 1) : shiftPeriod(d, range, 1))}
                aria-label={t("home1.next_day")}
                title={t("home1.next_day")}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Range mode */}
          <div className="space-y-1">
            <Label>&nbsp;</Label>
            <div className="flex flex-wrap gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1">
              {(["day", "week", "month", "quarter", "year"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setRange(m)}
                  className={cn(
                    "rounded-md px-3 py-1.5 text-sm transition-colors",
                    range === m ? "bg-white shadow-sm font-medium" : "text-slate-600 hover:text-slate-900",
                  )}
                >
                  {t(`home1.range_${m}`)}
                </button>
              ))}
            </div>
          </div>

          {/* View mode (pair/plan/fact/delta) */}
          <div className="space-y-1">
            <Label>&nbsp;</Label>
            <div className="flex flex-wrap gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1">
              {(["pair", "plan", "fact", "delta"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setView(m)}
                  className={cn(
                    "rounded-md px-3 py-1.5 text-sm transition-colors",
                    view === m ? "bg-white shadow-sm font-medium" : "text-slate-600 hover:text-slate-900",
                  )}
                >
                  {t(`home1.view_${m}`)}
                </button>
              ))}
            </div>
          </div>

        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 text-sm text-slate-500">{t("common.loading")}</div>
          ) : !hasData ? (
            <div className="p-6 text-sm text-slate-500">{t("home1.no_data")}</div>
          ) : range === "day" ? (
            <HourTable rows={hourlyRows} view={view} />
          ) : (
            <DayTable rows={dailyRows} view={view} />
          )}
        </CardContent>
      </Card>

      {range === "day" && <CostCurveWidget date={date} />}
    </div>
  );
}

// ────────────────────────────── Hourly table ─────────────────────────────

interface HourTableProps {
  rows: MetricsHourly[];
  view: ViewMode;
}

export function HourTable({ rows, view }: HourTableProps) {
  const { t } = useTranslation();

  const totals = useMemo(() => {
    let planRev = 0, factRev = 0, planProf = 0, factProf = 0;
    let planEko = 0, factEko = 0, planRdn = 0, factRdn = 0;
    let planRevEko = 0, factRevEko = 0, planRevRdn = 0, factRevRdn = 0;
    let planProfEko = 0, factProfEko = 0, planProfRdn = 0, factProfRdn = 0;
    let planGas = 0, factGas = 0;
    let imbPos = 0, imbNeg = 0, selfNeeds = 0;
    for (const r of rows) {
      planGas += num(r.plan_gas_m3) ?? 0;
      factGas += num(r.fact_gas_m3) ?? 0;
      planRev += num(r.plan_revenue_uah) ?? 0;
      factRev += num(r.fact_revenue_uah) ?? 0;
      planProf += num(r.plan_profit_uah) ?? 0;
      factProf += num(r.fact_profit_uah) ?? 0;
      planEko += num(r.plan_volumes?.ekosfera ?? null) ?? 0;
      factEko += num(r.fact_volumes?.ekosfera ?? null) ?? 0;
      planRdn += num(r.plan_volumes?.rdn ?? null) ?? 0;
      factRdn += num(r.fact_volumes?.rdn ?? null) ?? 0;
      planRevEko += num(r.plan_revenue_ekosfera_uah) ?? 0;
      factRevEko += num(r.fact_revenue_ekosfera_uah) ?? 0;
      planRevRdn += num(r.plan_revenue_rdn_uah) ?? 0;
      factRevRdn += num(r.fact_revenue_rdn_uah) ?? 0;
      planProfEko += num(r.plan_profit_ekosfera_uah) ?? 0;
      factProfEko += num(r.fact_profit_ekosfera_uah) ?? 0;
      planProfRdn += num(r.plan_profit_rdn_uah) ?? 0;
      factProfRdn += num(r.fact_profit_rdn_uah) ?? 0;
      selfNeeds += num(r.fact_self_needs_kwh) ?? 0;
      // Небаланс: «+» = надлишок експорту (A− > план); «−» = недовідпуск ПЛЮС
      // незапланований імпорт з мережі (A+ як від'ємний небаланс — дефіцит).
      // export і import не схлопуються (settlement роздільний).
      const ex = num(r.imbalance_export_kwh);
      if (ex !== null) {
        if (ex > 0) imbPos += ex;
        else if (ex < 0) imbNeg += ex;
      }
      const im = num(r.imbalance_import_kwh);
      if (im !== null) imbNeg += -im;
    }
    return {
      planRev, factRev, planProf, factProf,
      planEko, factEko, planRdn, factRdn,
      planRevEko, factRevEko, planRevRdn, factRevRdn,
      planProfEko, factProfEko, planProfRdn, factProfRdn,
      planGas, factGas,
      imbPos, imbNeg, selfNeeds,
    };
  }, [rows]);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50">
          <tr className="text-left text-slate-500">
            <th className="p-2 w-16">{t("common.hour")}</th>
            <Th view={view} label={t("home1.power_kw")} />
            <Th view={view} label={t("home1.gas_m3")} />
            <Th view={view} label={t("home1.volume_ekosfera")} />
            <Th view={view} label={t("home1.volume_rdn")} />
            <Th view={view} label={t("home1.revenue_rdn")} />
            <Th view={view} label={t("home1.profit_rdn")} />
            <Th view={view} label={t("home1.revenue_ekosfera")} />
            <Th view={view} label={t("home1.profit_ekosfera")} />
            <Th view={view} label={t("home1.revenue")} />
            <Th view={view} label={t("home1.profit")} />
            <th className="p-2 text-right">{t("home1.self_needs")}</th>
            <th className="p-2 text-right">{t("home1.imbalance_pos")}</th>
            <th className="p-2 text-right">{t("home1.imbalance_neg")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.ts}
              className={
                "border-t border-slate-100 " +
                (r.had_alarm ? "bg-red-50" : r.had_warning ? "bg-amber-50" : "")
              }
            >
              <td className="p-2 font-mono">
                <span className="flex items-center gap-1">
                  {hourLabel(r.ts)}
                  {r.direction_anomaly && (
                    <span title="FR-METER-DIR: аномалія напрямку лічильника" className="text-amber-600">⚠</span>
                  )}
                  {r.had_alarm && <span title="ГГУ Аварія" className="text-red-700">🚨</span>}
                  {!r.had_alarm && r.had_warning && (
                    <span title="ГГУ Попередження" className="text-amber-700">⚡</span>
                  )}
                </span>
              </td>
              <Cell view={view} plan={num(r.plan_power_kw)} fact={num(r.fact_power_kw)} zeroLabel="Off" />
              {/* Газ: план — з кривої `kgu_gas_curve`, факт — з лічильника,
                  якщо джерело `gas_web_cabinet` віддало годину (FR-CALC-02b). */}
              <Cell view={view} plan={num(r.plan_gas_m3)} fact={num(r.fact_gas_m3)} digits={1} />
              <Cell
                view={view}
                plan={num(r.plan_volumes?.ekosfera ?? null)}
                fact={num(r.fact_volumes?.ekosfera ?? null)}
              />
              <Cell
                view={view}
                plan={num(r.plan_volumes?.rdn ?? null)}
                fact={num(r.fact_volumes?.rdn ?? null)}
              />
              <Cell view={view} plan={num(r.plan_revenue_rdn_uah)} fact={num(r.fact_revenue_rdn_uah)} />
              <Cell view={view} plan={num(r.plan_profit_rdn_uah)} fact={num(r.fact_profit_rdn_uah)} signed />
              <Cell view={view} plan={num(r.plan_revenue_ekosfera_uah)} fact={num(r.fact_revenue_ekosfera_uah)} />
              <Cell view={view} plan={num(r.plan_profit_ekosfera_uah)} fact={num(r.fact_profit_ekosfera_uah)} signed />
              <Cell view={view} plan={num(r.plan_revenue_uah)} fact={num(r.fact_revenue_uah)} />
              <Cell view={view} plan={num(r.plan_profit_uah)} fact={num(r.fact_profit_uah)} signed />
              {/* Власні потреби — завжди факт (плану немає), незалежно від перемикача. */}
              <td className="p-2 text-right font-mono">{fmt(num(r.fact_self_needs_kwh))}</td>
              {/* Небаланс: «+» = надлишок експорту (A− > план); «−» = недовідпуск
                  ПЛЮС незапланований імпорт з мережі (A+ як від'ємний — дефіцит).
                  export і import не схлопуються (settlement роздільний). */}
              {(() => {
                const ex = num(r.imbalance_export_kwh);
                const im = num(r.imbalance_import_kwh);
                const pos = ex !== null && ex > 0 ? ex : 0;
                let neg = ex !== null && ex < 0 ? ex : 0;
                if (im !== null) neg += -im;
                return (
                  <>
                    <td className="p-2 text-right font-mono text-emerald-700">
                      {pos > 0 ? "+" + fmt(pos) : ""}
                    </td>
                    <td className="p-2 text-right font-mono text-red-700">
                      {neg < 0 ? fmt(neg) : ""}
                    </td>
                  </>
                );
              })()}
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-slate-50 font-medium">
          <tr>
            <td className="p-2">{t("common.total")}</td>
            {/* Power: суму не показуємо (безглуздо як Σ кВт). */}
            <td className="p-2" colSpan={view === "pair" ? 2 : 1} />
            {/* Кожна колонка шапки повинна мати комірку тут — інакше всі підсумки зсуваються. */}
            <SumCell view={view} plan={totals.planGas} fact={totals.factGas} digits={1} />
            <SumCell view={view} plan={totals.planEko} fact={totals.factEko} />
            <SumCell view={view} plan={totals.planRdn} fact={totals.factRdn} />
            <SumCell view={view} plan={totals.planRevRdn} fact={totals.factRevRdn} />
            <SumCell view={view} plan={totals.planProfRdn} fact={totals.factProfRdn} signed />
            <SumCell view={view} plan={totals.planRevEko} fact={totals.factRevEko} />
            <SumCell view={view} plan={totals.planProfEko} fact={totals.factProfEko} signed />
            <SumCell view={view} plan={totals.planRev} fact={totals.factRev} />
            <SumCell view={view} plan={totals.planProf} fact={totals.factProf} signed />
            <td className="p-2 text-right font-mono">{fmt(totals.selfNeeds)}</td>
            <td className="p-2 text-right font-mono text-emerald-700">
              {totals.imbPos > 0 ? "+" + fmt(totals.imbPos) : ""}
            </td>
            <td className="p-2 text-right font-mono text-red-700">
              {totals.imbNeg < 0 ? fmt(totals.imbNeg) : ""}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ────────────────────────────── Daily table ──────────────────────────────

interface DayTableProps {
  rows: DailyAgg[];
  view: ViewMode;
}

function DayTable({ rows, view }: DayTableProps) {
  const { t } = useTranslation();
  const totals = useMemo(() => {
    let planRev = 0,
      factRev = 0,
      planProf = 0,
      factProf = 0,
      planEko = 0,
      factEko = 0,
      planRdn = 0,
      factRdn = 0,
      factGen = 0;
    for (const r of rows) {
      planRev += num(r.plan_revenue_uah) ?? 0;
      factRev += num(r.fact_revenue_uah) ?? 0;
      planProf += num(r.plan_profit_uah) ?? 0;
      factProf += num(r.fact_profit_uah) ?? 0;
      planEko += num(r.plan_volume_ekosfera) ?? 0;
      factEko += num(r.fact_volume_ekosfera) ?? 0;
      planRdn += num(r.plan_volume_rdn) ?? 0;
      factRdn += num(r.fact_volume_rdn) ?? 0;
      factGen += num(r.fact_generation_kwh) ?? 0;
    }
    return { planRev, factRev, planProf, factProf, planEko, factEko, planRdn, factRdn, factGen };
  }, [rows]);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50">
          <tr className="text-left text-slate-500">
            <th className="p-2 w-28">{t("home1.col_date")}</th>
            <th className="p-2 text-right">{t("home1.generation_kwh")}</th>
            <Th view={view} label={t("home1.volume_ekosfera")} />
            <Th view={view} label={t("home1.volume_rdn")} />
            <Th view={view} label={t("home1.revenue")} />
            <Th view={view} label={t("home1.profit")} />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.day} className="border-t border-slate-100">
              <td className="p-2 font-mono">{r.day.slice(0, 10)}</td>
              <td className="p-2 text-right font-mono">{fmt(num(r.fact_generation_kwh))}</td>
              <Cell view={view} plan={num(r.plan_volume_ekosfera)} fact={num(r.fact_volume_ekosfera)} />
              <Cell view={view} plan={num(r.plan_volume_rdn)} fact={num(r.fact_volume_rdn)} />
              <Cell view={view} plan={num(r.plan_revenue_uah)} fact={num(r.fact_revenue_uah)} />
              <Cell view={view} plan={num(r.plan_profit_uah)} fact={num(r.fact_profit_uah)} />
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-slate-50 font-medium">
          <tr>
            <td className="p-2">{t("common.total")}</td>
            <td className="p-2 text-right font-mono">{fmt(totals.factGen)}</td>
            <SumCell view={view} plan={totals.planEko} fact={totals.factEko} />
            <SumCell view={view} plan={totals.planRdn} fact={totals.factRdn} />
            <SumCell view={view} plan={totals.planRev} fact={totals.factRev} />
            <SumCell view={view} plan={totals.planProf} fact={totals.factProf} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ──────────────────────────── Shared cells ──────────────────────────────

function Th({ view, label }: { view: ViewMode; label: string }) {
  if (view === "pair") {
    return (
      <th className="p-2 text-right" colSpan={2}>
        {label}
      </th>
    );
  }
  return <th className="p-2 text-right">{label}</th>;
}

/** Колір для signed-числа: + зелений, − червоний, 0/null — без кольору. */
function signedClass(v: number | null): string {
  if (v === null) return "";
  if (v > 0) return "text-emerald-700";
  if (v < 0) return "text-red-700";
  return "";
}

function Cell({
  view, plan, fact, signed = false, zeroLabel, digits = 0,
}: {
  view: ViewMode; plan: number | null; fact: number | null;
  signed?: boolean; zeroLabel?: string; digits?: number;
}) {
  // zeroLabel: для колонки потужності 0 = «Off» (КГУ вимкнено), а не просто 0.
  const show = (v: number | null) => (zeroLabel && v === 0 ? zeroLabel : fmt(v, digits));
  if (view === "plan") {
    return <td className={cn("p-2 text-right font-mono", signed && signedClass(plan))}>{show(plan)}</td>;
  }
  if (view === "fact") {
    return <td className={cn("p-2 text-right font-mono", signed && signedClass(fact))}>{show(fact)}</td>;
  }
  if (view === "delta") {
    const d = plan !== null && fact !== null ? fact - plan : null;
    return (
      <td className={cn("p-2 text-right font-mono", signedClass(d))}>
        {d === null ? "—" : (d > 0 ? "+" : "") + fmt(d, digits)}
      </td>
    );
  }
  // pair
  return (
    <>
      <td className={cn("p-2 text-right font-mono", signed ? signedClass(plan) : "text-slate-500")}>{show(plan)}</td>
      <td className={cn("p-2 text-right font-mono", signed && signedClass(fact))}>{show(fact)}</td>
    </>
  );
}

function SumCell({
  view, plan, fact, signed = false, digits = 0,
}: { view: ViewMode; plan: number; fact: number; signed?: boolean; digits?: number }) {
  if (view === "plan") {
    return <td className={cn("p-2 text-right font-mono", signed && signedClass(plan))}>{fmt(plan, digits)}</td>;
  }
  if (view === "fact") {
    return <td className={cn("p-2 text-right font-mono", signed && signedClass(fact))}>{fmt(fact, digits)}</td>;
  }
  if (view === "delta") {
    const d = fact - plan;
    return (
      <td
        className={cn(
          "p-2 text-right font-mono",
          d > 0 ? "text-emerald-700" : d < 0 ? "text-red-700" : "",
        )}
      >
        {(d > 0 ? "+" : "") + fmt(d, digits)}
      </td>
    );
  }
  return (
    <>
      <td className={cn("p-2 text-right font-mono", signed ? signedClass(plan) : "text-slate-500")}>{fmt(plan, digits)}</td>
      <td className={cn("p-2 text-right font-mono", signed && signedClass(fact))}>{fmt(fact, digits)}</td>
    </>
  );
}
